import idx from 'idx';

import Group from 'entities/Group';
// @ts-ignore
import { rootGroupCode } from 'entities/RootGroup';
// @ts-ignore
import GroupStore from 'groups/stores/GroupStore';
import RemindAPI from 'lib/RemindAPI';

import {
    LoadByGroupIdQuery, LoadByGroupIdQuery_nonV2ClassById, LoadByGroupIdQueryVariables
} from 'groups/queries/__generated__/LoadByGroupIdQuery';
import {
    LoadGroupByCode, LoadGroupByCode_classes
} from 'groups/queries/__generated__/LoadGroupByCode';
import {
    GroupByCode, GroupByCode_groupsByCode
} from 'groups/queries/__generated__/GroupByCode';
import {
    GroupByUUID, GroupByUUID_nonV2Class
} from 'groups/queries/__generated__/GroupByUUID';
import {
    LoadOwnedGroupSummaries, LoadOwnedGroupSummaries_me_classes
} from 'groups/queries/__generated__/LoadOwnedGroupSummaries';
import { LoadStaffClassForOrgId } from 'groups/queries/__generated__/LoadStaffClassForOrgId';

import GROUP_BY_CODE_QUERY from 'groups/queries/LoadByGroupCodeQuery.gql';
import GROUP_BY_ID_NONV2 from 'groups/queries/LoadByGroupIdQuery.gql';
import NONV2GROUP_BY_CODE_QUERY from 'groups/queries/GroupByCodeQuery.gql';
import NONV2GROUP_BY_UUID_QUERY from 'groups/queries/GroupByUUIDQuery.gql';
import OWNED_GROUP_SUMMARIES_QUERY from 'groups/queries/LoadOwnedGroupSummariesQuery.gql';
import STAFF_CLASS_FOR_ORG_QUERY from 'groups/queries/LoadStaffClassForOrgIdQuery.gql';

export { GroupByUUID_nonV2Class } from 'groups/queries/__generated__/GroupByUUID';
export { GroupByCode_groupsByCode } from 'groups/queries/__generated__/GroupByCode';

export function loadGQLUserOwnedGroups(
  forceFetch: boolean = false
): Promise<(LoadOwnedGroupSummaries_me_classes | null)[] | undefined> {
  return RemindAPI.execute<LoadOwnedGroupSummaries>({
    query: OWNED_GROUP_SUMMARIES_QUERY,
    forceFetch,
  }).then(
    res => {
      return res.me ? res.me.classes.filter(c => c && c.type === 'owned') : undefined;
    },
    err => {
      console.warn(err);
      console.error('loadGQLUserOwnedGroups failure');
      return undefined;
    }
  );
}

export function loadGQLUserGroupById(
  ids: string,
  forceFetch: boolean = false
): Promise<LoadByGroupIdQuery_nonV2ClassById | undefined> {
  return RemindAPI.execute<LoadByGroupIdQuery>({
    query: GROUP_BY_ID_NONV2,
    variables: { ids },
    forceFetch,
  })
    .then(
      res => {
        return res.nonV2ClassById || undefined;
      },
      () => {
        return undefined;
      }
    )
    .catch(err => {
      console.warn(err);
      console.error('loadGQLUserGroupById failure');
      return undefined;
    });
}

export function loadGQLUserGroupByCode(
  groupCode: string,
  forceFetch: boolean = false
): Promise<LoadGroupByCode_classes | undefined> {
  return RemindAPI.execute<LoadGroupByCode>({
    query: GROUP_BY_CODE_QUERY,
    variables: { code: `@${groupCode}` },
    forceFetch,
  })
    .then(
      res => {
        return res.classes[0] || undefined;
      },
      () => {
        return undefined;
      }
    )
    .catch(err => {
      console.warn(err);
      console.error('loadGQLUserGroupByCode failure');
      return undefined;
    });
}

// DEPRECATED: try not to use loadStaffClassForOrgId, since it returns a Group entity and not a GQL object
export function loadStaffClassForOrgId(id: number): Promise<Group | undefined> {
  return RemindAPI.execute<LoadStaffClassForOrgId>({
    query: STAFF_CLASS_FOR_ORG_QUERY,
    variables: { id },
  }).then(
    res => {
      const { organizations } = res;
      if (!organizations) {
        return undefined;
      }
      const org = organizations[0];
      const group = org ? org.class : undefined;
      if (group) {
        return Group.fromJSON(group) as Group;
      }
      return undefined;
    },
    err => {
      console.warn(err);
      console.error('loadStaffClassForOrgId failure');
      return undefined;
    }
  );
}

export function loadNonV2ClassByUUID(
  uuid: string,
  forceFetch: boolean = false
): Promise<GroupByUUID_nonV2Class | void> {
  return RemindAPI.execute<GroupByUUID>({
    query: NONV2GROUP_BY_UUID_QUERY,
    variables: { uuid },
    forceFetch,
  }).then(
    (res: GroupByUUID) => res.nonV2Class,
    err => {
      console.warn(err);
      console.log('loadNonV2ClassByUUID failure');
      return;
    }
  );
}

export function loadNonV2GroupByCode(
  code: string,
  forceFetch: boolean = false
): Promise<GroupByCode_groupsByCode | void> {
  if (code === rootGroupCode) {
    return Promise.resolve(GroupStore.getGroupForCode(code));
  }

  return RemindAPI.execute<GroupByCode>({
    query: NONV2GROUP_BY_CODE_QUERY,
    variables: { code: `@${code}` },
    forceFetch,
  }).then(
    (res: GroupByCode) => {
      return res.groupsByCode[0] || undefined;
    },
    err => {
      console.warn(err);
      console.log('GroupByCode failure');
      return;
    }
  );
}

// DEPRECATED: try not to use loadGroupByCode, since it returns a Group entity and not a GQL object
export function loadGroupByCode(
  groupCode: string,
  forceFetch: boolean = false
): Promise<Group | undefined> {
  // this is critical for the root group,
  // as it is always returned here.
  if (!forceFetch || groupCode === rootGroupCode) {
    const group = GroupStore.getGroupForCode(groupCode);
    if (group) {
      return Promise.resolve(group);
    }
  }

  return RemindAPI.execute<LoadGroupByCode>({
    query: GROUP_BY_CODE_QUERY,
    variables: { code: `@${groupCode}` },
    forceFetch,
  }).then(
    res => {
      return Group.fromJSON(res.classes[0]) as Group;
    },
    err => {
      const code = idx(err.graphQLErrors[0], _ => _.serviceMetadata.service_response.code);

      if (code === 'class_not_found') {
        return undefined;
      }

      console.warn(err);
      console.error('LoadGroupByCode failure');
      return undefined;
    }
  );
}
